/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import {
  GetAttributesFormResponse,
  ResponseAttributesFormItemsProps,
} from '@/auth/types';
import { AttributesFormModel } from '../models';
import { convertKeysCase, convertToCamelCase } from '@/auth/lib/convertCase';

export const cloneArrayIfExists = (
  fields: ResponseAttributesFormItemsProps[]
) => {
  let multilineItems: any = [];

  for (const element of fields) {
    if (element.frontend_input !== 'MULTILINE' || element.multiline_count < 2) {
      continue;
    }

    for (let i = 2; i <= element.multiline_count; i++) {
      const newItem = {
        ...element,
        is_required: false,
        name: `${element.code}_multiline_${i}`,
        code: `${element.code}_multiline_${i}`,
        id: `${element.code}_multiline_${i}`,
      };

      multilineItems.push(newItem);
    }
  }

  return multilineItems;
};

export const transformAttributesForm = (
  response: GetAttributesFormResponse
): AttributesFormModel[] => {
  const items = response?.data?.attributesForm?.items || [];

  if (!items.length) return [];

  const fields = items
    .filter((el) => !el.frontend_input?.includes('HIDDEN'))
    ?.map(({ code, ...other }) => {
      const isDefaultCode = code !== 'country_id' ? code : 'country_code';

      return {
        ...other,
        name: isDefaultCode,
        id: isDefaultCode,
        code: isDefaultCode,
      };
    });

  const multilineItems = cloneArrayIfExists(fields as any);

  const attributesConfig = fields
    .concat(multilineItems)
    .map((item) => {
      const customUpperCode =
        item.code === 'firstname'
          ? 'firstName'
          : item.code === 'lastname'
          ? 'lastName'
          : convertToCamelCase(item.code);

      const options = item.options?.map((el) => {
        return {
          isDefault: el.is_default,
          text: el.label,
          value: el.value,
        };
      });

      return convertKeysCase(
        { ...item, options, customUpperCode },
        'camelCase',
        {
          frontend_input: 'fieldType',
          frontend_class: 'className',
          is_required: 'required',
          sort_order: 'orderNumber',
        }
      );
    })
    .sort(
      (a: AttributesFormModel, b: AttributesFormModel) =>
        a.orderNumber - b.orderNumber
    );

  return attributesConfig;
};
